import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { TreatmentCatalogueEntry } from '@borradh-workspace/labels';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
const needling = new Set([
  'microneedling',
  'rf_microneedling',
  'spicule_needling',
  'nano_infusion',
  'morpheus8',
  'intracel',
]);

/** Context orders the full catalogue, never filters by stock or saves a guess. */
export function contextualTreatments(
  name: string,
  catalogue: TreatmentCatalogueEntry[]
) {
  const text = ` ${normalize(name)} `;
  const direct = catalogue.filter((entry) =>
    [entry.displayName, ...entry.aliases].some((term) => {
      const phrase = normalize(term);
      return phrase.length >= 3 && text.includes(` ${phrase} `);
    })
  );
  const needlingContext =
    direct.some((entry) => needling.has(entry.slug)) ||
    /micro\s*needling|nano\s*needling|spicule/.test(text);
  return catalogue.filter(
    (entry) =>
      direct.includes(entry) ||
      (needlingContext && needling.has(entry.slug)) ||
      direct.some(
        (match) =>
          match.slug === entry.parentSlug ||
          entry.slug === match.parentSlug ||
          match.techniqueSlug === entry.techniqueSlug
      )
  );
}

export function ServiceTreatmentPicker({
  id,
  value,
  catalogue,
  serviceName,
  recommendedSlugs,
  disabled,
  onChange,
}: {
  id: string;
  value: string | null;
  catalogue: TreatmentCatalogueEntry[];
  serviceName: string;
  recommendedSlugs?: string[];
  disabled: boolean;
  onChange: (value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = catalogue.find((entry) => entry.slug === value);
  const orderedCatalogue = [...catalogue].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );
  const suggestedIds = new Set([
    ...(recommendedSlugs ?? []),
    ...contextualTreatments(serviceName, catalogue).map((entry) => entry.slug),
  ]);
  const bySlug = new Map(catalogue.map((entry) => [entry.slug, entry]));
  const suggested = [...suggestedIds].flatMap((slug) => {
    const entry = bySlug.get(slug);
    return entry ? [entry] : [];
  });
  const choose = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };
  const item = (entry: TreatmentCatalogueEntry) => (
    <CommandItem
      key={entry.slug}
      value={`${entry.displayName} ${entry.slug} ${entry.aliases.join(' ')}`}
      onSelect={() => choose(entry.slug)}
    >
      <Check
        className={cn(
          'mr-2 size-4',
          value === entry.slug ? 'opacity-100' : 'opacity-0'
        )}
      />
      {entry.displayName}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-12 w-full justify-between rounded-xl bg-background px-3.5 font-normal shadow-none"
        >
          <span className="truncate">
            {selected?.displayName ?? (value === 'other' ? 'Other' : 'Not set')}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command
          filter={(value, search) =>
            normalize(search)
              .split(/\s+/)
              .every((term) => normalize(value).includes(term))
              ? 1
              : 0
          }
        >
          <CommandInput placeholder="Search treatments…" />
          <CommandList>
            <CommandEmpty>No treatment matches.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="not set null" onSelect={() => choose(null)}>
                <Check
                  className={cn(
                    'mr-2 size-4',
                    value === null ? 'opacity-100' : 'opacity-0'
                  )}
                />
                Not set
              </CommandItem>
              <CommandItem
                value="other unlisted treatment"
                onSelect={() => choose('other')}
              >
                <Check
                  className={cn(
                    'mr-2 size-4',
                    value === 'other' ? 'opacity-100' : 'opacity-0'
                  )}
                />
                Other
              </CommandItem>
            </CommandGroup>
            {suggested.length ? (
              <CommandGroup heading="Related to this service">
                {suggested.map(item)}
              </CommandGroup>
            ) : null}
            <CommandGroup
              heading={
                suggested.length ? 'All other treatments' : 'All treatments'
              }
            >
              {orderedCatalogue
                .filter((entry) => !suggestedIds.has(entry.slug))
                .map(item)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
